import * as Sentry from '@sentry/react';

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || 'development';

  // Debug logging
  console.log('🔍 Sentry Init Debug:', {
    hasDSN: !!dsn,
    dsnPreview: dsn ? dsn.substring(0, 30) + '...' : 'missing',
    environment,
    willEnable: environment !== 'development' || import.meta.env.VITE_SENTRY_ENABLED === 'true'
  });

  // Only initialize Sentry if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not provided. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in production, 100% in dev
    // Session Replay
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 0, // 10% in production
    replaysOnErrorSampleRate: 1.0, // Always capture replays when errors occur
    
    // Don't send errors in development unless explicitly enabled
    enabled: environment !== 'development' || import.meta.env.VITE_SENTRY_ENABLED === 'true',
    
    // Filter out specific errors
    beforeSend(event, hint) {
      // Filter out network errors from localhost
      if (event.request?.url?.includes('localhost')) {
        return null;
      }
      
      // Log the error for debugging
      console.error('Sentry captured error:', hint.originalException || hint.syntheticException);
      
      return event;
    },
  });

  console.log('✅ Sentry initialized successfully');
};

// Helper to manually capture errors
export const captureError = (error: Error, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

// Helper to set user context
export const setUserContext = (user: { id: string; email?: string; username?: string }) => {
  Sentry.setUser(user);
};

// Helper to clear user context (on logout)
export const clearUserContext = () => {
  Sentry.setUser(null);
};

// Helper to add custom breadcrumb (for tracking user actions)
export const addBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
};

// Helper to set context tags (subscription tier, project info, etc.)
export const setContextTags = (tags: Record<string, string>) => {
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });
};

// Helper to show user feedback dialog
export const showUserFeedback = () => {
  Sentry.showReportDialog();
};
